import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { ColetaMensal, UserCollectionTask } from '@core/models/indicator.model';

@Injectable({ providedIn: 'root' })
export class CollectionService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}/api/coletas`;

  readonly pendingTasks = signal<UserCollectionTask[]>([]);
  readonly historicalTasks = signal<UserCollectionTask[]>([]);
  readonly loading = signal(false);

  async loadPendingTasks(): Promise<void> {
    this.loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<any[]>(`${this.apiUrl}/pendentes`));
      this.pendingTasks.set(this.sortChronologically((data || []).map(this.normalizeTask)));
    } finally {
      this.loading.set(false);
    }
  }

  async loadHistoricalTasks(): Promise<void> {
    this.loading.set(true);
    try {
      const data = await lastValueFrom(this.http.get<any[]>(`${this.apiUrl}/historico`));
      this.historicalTasks.set(this.sortChronologically((data || []).map(this.normalizeTask)));
    } finally {
      this.loading.set(false);
    }
  }

  private normalizeTask(raw: any): UserCollectionTask {
    return {
      coletaId: raw.coletaId ?? raw.coleta_id ?? 0,
      indicadorId: raw.indicadorId ?? raw.indicador_id ?? '',
      sigla: raw.sigla ?? '',
      nome: raw.nome ?? '',
      unidade: raw.unidade ?? raw.unidade_medida ?? '',
      mesAno: raw.mesAno ?? raw.mes_ano ?? '',
      anoReferencia: raw.anoReferencia ?? raw.ano_referencia ?? null,
      mesReferencia: raw.mesReferencia ?? raw.mes_referencia ?? null,
      periodicidade: raw.periodicidade ?? 'MENSAL',
      valor: raw.valor ?? raw.valor_informado ?? null,
      status: raw.status ?? 'Pendente',
    };
  }

  private sortChronologically(tasks: UserCollectionTask[]): UserCollectionTask[] {
    return [...tasks].sort((a, b) => {
      const byReference = this.referenceOrder(a) - this.referenceOrder(b);
      if (byReference !== 0) return byReference;
      return a.sigla.localeCompare(b.sigla, 'pt-BR', { numeric: true });
    });
  }

  private referenceOrder(task: UserCollectionTask): number {
    if (task.mesAno) {
      const match = /^(\d{4})-(\d{2})/.exec(task.mesAno);
      if (match) return Number(match[1]) * 12 + Number(match[2]) - 1;
    }
    if (task.anoReferencia != null) return Number(task.anoReferencia) * 12;
    return Number.MAX_SAFE_INTEGER;
  }

  async saveCollection(coletaId: number, valor: number): Promise<void> {
    await lastValueFrom(this.http.put<void>(`${this.apiUrl}/${coletaId}`, { valor_informado: valor }));
  }

  async saveNewHistoricalRecord(indicadorId: string, periodicidade: 'MENSAL' | 'ANUAL', referencia: string, valor: number): Promise<void> {
    await lastValueFrom(this.http.post<void>(`${this.apiUrl}/historico-manual`, {
      indicador_id: indicadorId,
      periodicidade,
      mes_ano: periodicidade === 'MENSAL' ? referencia : null,
      ano_referencia: periodicidade === 'ANUAL' ? Number(referencia) : null,
      valor: valor
    }));
  }

  getHistoricalData(indicadorId: string): Promise<ColetaMensal[]> {
    return lastValueFrom(this.http.get<ColetaMensal[]>(`${this.apiUrl}/indicador/${indicadorId}/historico`));
  }

  removeTaskFromList(coletaId: number): void {
    this.pendingTasks.update(tasks => tasks.filter(t => t.coletaId !== coletaId));
  }
}
